'use client';
import { useCallback } from 'react';

import { gql, useMutation } from '@nerdwallet/apollo';

import { EarnRewardResponse } from '@generated-gql/generated-types';
import { RewardableEventType } from '@lib/types/nerdwallet-plus-rewards';
import { nwPlusEphemeralAlertQueueVar } from '@lib/apollo/nerdwallet-plus/reactive-vars';
import { useFeatureFlag } from '@nerdwallet/features';
import { FEATURE_FLAGS } from '@constants/feature-flags';
import { useIsNerdWalletPlusMember } from './use-is-nerdwallet-plus-member';

const EMIT_REWARDABLE_EVENT_MUTATION = gql`
  mutation EmitRewardableEvent($type: String!, $payload: JSON) {
    earnReward(type: $type, payload: $payload) {
      eventId
      status
      points
    }
  }
`;

interface Props {
  onSuccess?(response: EarnRewardResponse): void;
}

const useEmitRewardableEvent = ({ onSuccess }: Partial<Props> = {}): [
  (eventType?: RewardableEventType | null, eventPayload?: any) => void,
] => {
  const skipEphemeralAlertsFF = useFeatureFlag({
    featureFlagName: FEATURE_FLAGS.SKIP_EPHEMERAL_ALERT_FREEMIUM,
  });

  const { isNerdWalletPlusMember } = useIsNerdWalletPlusMember();

  const handleEmitRewardCompleted = useCallback(
    (response, clientOptions) => {
      const rewardPoints = response.earnReward?.points ?? 0;
      const eventType = clientOptions?.variables?.type;

      if (
        rewardPoints > 0 &&
        (!skipEphemeralAlertsFF?.data?.value || isNerdWalletPlusMember)
      ) {
        const newEphemeralAlertQueue = new Set(nwPlusEphemeralAlertQueueVar());
        newEphemeralAlertQueue.add({
          eventType,
          rewardPoints,
        });
        nwPlusEphemeralAlertQueueVar(newEphemeralAlertQueue);
      }
      if (onSuccess) {
        onSuccess(response.earnReward);
      }
    },
    [isNerdWalletPlusMember, onSuccess, skipEphemeralAlertsFF?.data?.value],
  );

  const [emitReward] = useMutation(EMIT_REWARDABLE_EVENT_MUTATION, {
    onCompleted: handleEmitRewardCompleted,
    onError: () => {
      // catching error to avoid an Unhandled Runtime Error
    },
  });

  const emitGatedReward = useCallback(
    (eventType?: string | null, eventPayload?: any) => {
      if (eventType) {
        emitReward({
          variables: {
            type: eventType,
            payload: eventPayload,
          },
        });
      }
    },
    [emitReward],
  );

  return [emitGatedReward];
};

export default useEmitRewardableEvent;

export const mockUseEmitRewardableEvent = (
  eventType: RewardableEventType,
  eventPayload?: Record<string, any>,
) => ({
  request: {
    query: EMIT_REWARDABLE_EVENT_MUTATION,
    variables: {
      type: eventType,
      payload: eventPayload,
    },
  },
  result: {
    data: {
      earnReward: {
        eventId: '1234567',
        status: 'SUCCESS',
        points: 100,
      },
    },
  },
});
